import { useEffect, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { notifications } from "@mantine/notifications";
import {
  CommentType,
  SendOrderHelpRequestDocument,
} from "/@/generated/graphql";
import { useAuthContext } from "/@/shared/contexts/AuthContext";
import { useQrCodeCanvas } from "/@/components/order/useQrCodeCanvas";
import {
  GET_FILE_DOWNLOAD_URL,
  helpFileRoom,
  joinSocketRoom,
  onSocketEvent,
  type Order,
  DeleteOrderHelpFileDocument,
  GetOrderHelpFilesDocument,
} from "@jasper/shared";

type UseOrderHelpRequestParams = {
  order: Order;
  // Temporary comment id: the files updloaded from the phone are attached to it
  id: string;
  refetch: () => void;
  onSent: () => void;
};

// Help request "Commentaire" in V1, "Ajouter une précision" in V2:
// Free text + photos uploaded from the phone through a QR code
export const useOrderHelpRequest = ({
  order,
  id,
  refetch,
  onSent,
}: UseOrderHelpRequestParams) => {
  const [filesToShow, setFilesToShow] = useState<any[]>([]);
  const { user } = useAuthContext();
  const [helpRequestMessage, setHelpRequestMessage] = useState("");
  const qrCodeCanvasRef = useQrCodeCanvas(
    user?.accessToken
      ? `${window.location.origin}/#/order-help-upload-file/${order.id}/${order.patient?.lastName}/${user.accessToken}`
      : undefined
  );
  const [sendOrderHelpRequest, { error }] = useMutation(
    SendOrderHelpRequestDocument
  );

  const [deleteOrderHelpFile] = useMutation(DeleteOrderHelpFileDocument);
  const [getPresignedDownload] = useLazyQuery(GET_FILE_DOWNLOAD_URL);

  const deleteOneHelpFile = async (fileId: string) => {
    await deleteOrderHelpFile({ variables: { orderId: order.id, fileId } });
  };

  const getFileUrlOrDownloadFile = async (key: string) => {
    const res = await getPresignedDownload({
      variables: {
        key: key,
      },
    });
    return res?.data?.getPresignedFileDownloadUrl;
  };

  const { data, refetch: refetchHelpFiles } = useQuery(
    GetOrderHelpFilesDocument,
    { variables: { orderId: order.id } }
  );

  const files = data?.getOrderHelpFiles ?? [];

  useEffect(() => {
    const leaveRoom = joinSocketRoom(helpFileRoom(order.id));
    const unsubscribe = onSocketEvent("clientOrderHelpFile", () => {
      refetchHelpFiles();
    });

    return () => {
      unsubscribe();
      leaveRoom();
    };
  }, []);

  const submit = () => {
    localStorage.removeItem("help_temp_ID");
    const args = {
      id: id,
      order: {
        connect: {
          id: order.id,
        },
      },
      comment: helpRequestMessage,
      type: CommentType.Help,
    };

    sendOrderHelpRequest({
      variables: {
        args: args,
      },
    })
      .then(() => {
        notifications.show({
          color: "green",
          title: "Votre demande a bien été envoyée",
          message: "",
        });
        refetch();
        onSent();
      })
      .catch(error => {
        console.error(error);
        notifications.show({
          color: "red",
          title: "Erreur lors de l'envoi de la demande",
          message: "",
        });
      });
  };

  useEffect(() => {
    if (files.length > 0) {
      const updateFilesToShow = async () => {
        const updatedFiles = await Promise.all(
          files.map(async (file: any) => ({
            ...file,
            path: await getFileUrlOrDownloadFile(file.key),
          }))
        );
        setFilesToShow(updatedFiles);
      };
      updateFilesToShow();
    }
  }, [files]);

  return {
    qrCodeCanvasRef,
    hasFiles: files.length > 0,
    filesToShow,
    deleteOneHelpFile,
    refetchHelpFiles,
    setHelpRequestMessage,
    submit,
    error,
  };
};

export type OrderHelpRequest = ReturnType<typeof useOrderHelpRequest>;
